import { useIntl } from 'react-intl';
import CopyToClipboardButton from '@rossum/components/copy-to-clipboard/CopyToClipboardButton';
import { IconChevronDown } from '@rossum/ui/icons/tabler';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';

export type ErrorDetailsState = {
  status?: number;
  method?: string;
  requestUrl?: string;
  payload?: string;
  response?: string;
  requestId?: string;
};

type ErrorDetailsProps = {
  debug?: ErrorDetailsState;
  onExpandChange?: (expanded: boolean) => void;
};

type DetailFieldProps = {
  label: string;
  value: string | number;
  scrollable?: boolean;
};

const DetailField = ({ label, value, scrollable }: DetailFieldProps) => (
  <Stack spacing={0.5}>
    <Typography variant="caption" color="text.secondary" fontWeight="bold">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontFamily: 'monospace',
        p: 1,
        bgcolor: 'action.hover',
        borderRadius: borderRadius('s'),
        wordBreak: 'break-all',
        ...(scrollable && {
          whiteSpace: 'pre-wrap',
          maxHeight: '200px',
          overflow: 'auto',
        }),
      }}
    >
      {value}
    </Typography>
  </Stack>
);

const ErrorDetails = ({ debug, onExpandChange }: ErrorDetailsProps) => {
  const intl = useIntl();

  if (!debug) return null;

  const errorData = {
    requestId: debug.requestId,
    status: debug.status,
    method: debug.method,
    requestUrl: debug.requestUrl,
    payload: debug.payload,
    response: debug.response,
  };

  return (
    <Accordion
      onChange={(_, expanded) => onExpandChange?.(expanded)}
      sx={{
        maxWidth: '800px',
        width: '100%',
        border: 'none',
        boxShadow: 'none',
        '&:before': {
          display: 'none',
        },
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <SvgIcon fontSize="large">
            <IconChevronDown />
          </SvgIcon>
        }
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          pr={1}
        >
          <Typography variant="body2" color="text.secondary">
            {intl.formatMessage({ id: 'containers.maintenance.details' })}
          </Typography>
          <CopyToClipboardButton
            content={JSON.stringify(errorData, null, 2)}
            buttonProps={{ size: 'small', variant: 'text', color: 'secondary' }}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {debug.requestId && (
            <DetailField label="Request ID:" value={debug.requestId} />
          )}
          {debug.status !== undefined && (
            <DetailField label="Status:" value={debug.status} />
          )}
          {debug.method && <DetailField label="Method:" value={debug.method} />}
          {debug.requestUrl && (
            <DetailField label="Request URL:" value={debug.requestUrl} />
          )}
          {debug.payload && (
            <DetailField label="Payload:" value={debug.payload} scrollable />
          )}
          {debug.response && (
            <DetailField label="Response:" value={debug.response} scrollable />
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default ErrorDetails;
